import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Lock, LogIn, MessageSquare, Globe, Languages, DollarSign, Database, RefreshCw, TrendingUp, Users, Award, CreditCard, Inbox, BarChart3, MousePointerClick, GraduationCap, ShoppingBag, ShieldCheck, ShieldOff, KeyRound, X, Download, RotateCcw, AlertTriangle, Boxes, Activity, Eye, Sparkles, Megaphone } from 'lucide-react';
import CollapsibleSection from '../components/admin/CollapsibleSection';
import { QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import AcademyAdminPanel from './AcademyAdminPanel';
import ChurnRadarWidget from '../components/admin/ChurnRadarWidget';
import SessionsGeoMap from '../components/admin/SessionsGeoMap';
import CallbacksWidget from '../components/admin/CallbacksWidget';
import TopProductsWidget from '../components/admin/TopProductsWidget';
import SearchAnalyticsWidget from '../components/admin/SearchAnalyticsWidget';
import CrossDivisionPathsWidget from '../components/admin/CrossDivisionPathsWidget';
import CompareConversionWidget from '../components/admin/CompareConversionWidget';
import CompareRecoveryWidget from '../components/admin/CompareRecoveryWidget';
import VariantImageEditor from '../components/admin/VariantImageEditor';
import SecurityDigestCard from '../components/admin/SecurityDigestCard';
import CredentialsCheatsheetCard from '../components/admin/CredentialsCheatsheetCard';
import CronHealthWidget from '../components/admin/CronHealthWidget';
import UnifiedRevenueWidget from '../components/admin/UnifiedRevenueWidget';
import CrossDivisionTrafficWidget from '../components/admin/CrossDivisionTrafficWidget';
import ServicesTab from '../components/admin/ServicesTab';
import UsersTab from '../components/admin/UsersTab';
import CatalogTab from '../components/admin/CatalogTab';
import AdminTeamPanel from '../components/admin/AdminTeamPanel';
import DiscountsPanel from '../components/admin/DiscountsPanel';
import { useAuth } from '../context/AuthContext';
import { useAdminScope } from '../hooks/useAdminScope';
import { useDestructiveAuditWatcher } from '../hooks/useDestructiveAuditWatcher';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;
const ADMIN_PW_KEY = 'zhelta_admin_pw';

async function adminFetch(path, pw) {
  // Always send cookies — the backend now accepts EITHER a valid admin JWT
  // (set by /api/auth/login from the SuperAdminLogin or AcademyAdminPanel
  // bootstrap) OR the legacy `X-Admin-Password` header. This means the 4
  // division admins (super + systems + services + academy) can all use
  // `/admin` with their own password instead of needing the global
  // ADMIN_PASSWORD env value.
  const headers = {};
  if (pw) headers['X-Admin-Password'] = pw;
  const res = await fetch(`${API}${path}`, { headers, credentials: 'include' });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

export default function Admin() {
  // ROLE GUARD: students / parents / instructors must never reach the admin
  // dashboard. If a non-admin JWT user lands here we hard-redirect them to
  // the right portal — the password gate isn't even rendered.
  const { user, refreshUser } = useAuth();
  const ADMIN_HIERARCHY = new Set(['super_admin', 'admin', 'manager', 'staff']);
  if (user && user.role) {
    if (user.role === 'super_admin') {
      // Super admins live in the new unified `/admin/v2` console.
      return <Navigate to="/admin/v2" replace />;
    }
    if (!ADMIN_HIERARCHY.has(user.role)) {
      const dest =
        user.role === 'parent'      ? '/academy/parent' :
        user.role === 'instructor'  ? '/academy/instructor' :
                                      '/academy/dashboard';
      return <Navigate to={dest} replace />;
    }
  }
  // FORCE 2FA: every admin MUST have TOTP enabled before reaching any tab.
  // Render the AdminInner under a non-dismissable 2FA setup modal until
  // user.totp_enabled flips true (after they verify the 6-digit code →
  // accept recovery codes → /auth/me re-fetch sets totp_enabled=true).
  // ESCAPE HATCH: backend exposes `enforce_admin_2fa=false` when env
  // DISABLE_ADMIN_2FA=1 is set (QA/CI only). In that case skip the gate.
  // SAFETY: only render when we EXPLICITLY know enforcement is on AND
  // totp is off — treating `undefined` as "force gate" caused a UX
  // regression where admins got bounced to the QR modal between the
  // login response (no flags) and the /auth/me re-fetch (full payload).
  if (
    user && user.role === 'admin'
    && user.enforce_admin_2fa === true
    && user.totp_enabled === false
  ) {
    return <Force2FAGate onDone={() => { try { refreshUser && refreshUser(); } catch { /* ignore */ } }} />;
  }
  return <AdminInner />;
}

function Force2FAGate({ onDone }) {
  // Background card explaining why 2FA is mandatory + the modal on top.
  // The modal renders with `forced` so the X button is hidden — admins
  // cannot dismiss it without completing enrolment.
  const [, setTick] = useState(0);
  return (
    <div className="min-h-screen bg-[#0e0e10] text-white grid place-items-center px-4">
      <div className="max-w-[520px] w-full rounded-2xl bg-zinc-900 border border-zinc-800 p-8 text-center shadow-2xl">
        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#c9a227]/20 text-[#e6c35a] mb-4">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <h1 className="text-[22px] font-extrabold tracking-tight">Set up 2FA to continue</h1>
        <p className="mt-2 text-[13.5px] text-zinc-400 leading-relaxed">
          ZHELTA Admin requires two-factor authentication. The setup card below will
          generate a QR code — scan it with Microsoft Authenticator, Google
          Authenticator or 1Password, then enter the 6-digit code to unlock the dashboard.
        </p>
      </div>
      <TwoFactorSetupModal
        forced
        onClose={() => { /* no-op — modal cannot be closed without enrolling */ }}
        onSuccess={async () => {
          // Refresh the auth context so user.totp_enabled flips true
          // and this gate re-renders into <AdminInner />.
          try { await onDone(); } finally { setTick((t) => t + 1); }
        }}
      />
    </div>
  );
}

function AdminInner() {
  const scope = useAdminScope();
  // Real-time toast for destructive admin events triggered by other admins.
  // Super-admin only; backend endpoint already 403s for division admins so
  // the hook would no-op anyway, but skipping the polling saves cycles.
  useDestructiveAuditWatcher({
    enabled: scope.role === 'super_admin' && !scope.loading,
    currentEmail: scope.email,
  });
  const [pw, setPw] = useState('');
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fx, setFx] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [topMarkets, setTopMarkets] = useState(null);
  const [topMarketsDays, setTopMarketsDays] = useState(7);
  const [navClicks, setNavClicks] = useState(null);
  const [navClicksDays, setNavClicksDays] = useState(30);
  const [tab, setTab] = useState(() => {
    // Honour ?tab=academy from login redirect (admins land on the academy
    // sub-tab so they can immediately manage programs / users / enrollments).
    const sp = new URLSearchParams(window.location.search);
    const t = sp.get('tab');
    return ['overview', 'revenue', 'leads', 'services', 'users', 'catalog', 'academy', 'team', 'discounts'].includes(t) ? t : 'overview';
  });

  // Snap the active tab to the first allowed one whenever the scope (role)
  // changes. Backend already 403s out-of-scope endpoints — this just keeps
  // the URL/UI honest so a Systems admin pasting `?tab=services` doesn't
  // see a half-broken page.
  useEffect(() => {
    if (scope.loading) return;
    if (scope.allowedTabs.size === 0) return;  // legacy gate / unknown role
    if (!scope.allowedTabs.has(tab)) {
      const fallback = scope.allowedTabs.has('overview')
        ? 'overview'
        : Array.from(scope.allowedTabs)[0];
      setTab(fallback);
    }
  }, [scope.loading, scope.role, tab, scope.allowedTabs]);
  const [revenue, setRevenue] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txStatus, setTxStatus] = useState(''); // '' | 'paid' | 'pending' | 'expired'
  const [leads, setLeads] = useState([]);
  const [leadCounts, setLeadCounts] = useState({});
  const [leadKind, setLeadKind] = useState(''); // '' | 'book-demo' | 'consultation' | 'contact' | 'custom-build' | 'scheduled'
  const [revenueDays, setRevenueDays] = useState(0); // 0 = all time, else days

  // Auto-login using stored password OR an existing JWT cookie session.
  // Division admins land here from `/zhelta-internal/{systems|services|academy}`
  // with a valid JWT cookie but no legacy admin password — we still want
  // them to sail straight into the dashboard.
  useEffect(() => {
    document.title = 'ZHELTA Admin';
    let meta = document.head.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, nofollow');
    const stored = localStorage.getItem(ADMIN_PW_KEY);
    if (stored) {
      setPw(stored);
      void attemptLogin(stored);
    } else {
      // No stored password — try cookie-only auth (works for any role=admin
      // user who already has a valid JWT from a division login portal).
      void attemptLogin('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const attemptLogin = async (password) => {
    setLoading(true);
    setError('');
    try {
      const s = await adminFetch('/analytics/summary', password);
      const c = await adminFetch('/analytics/contacts?limit=50', password);
      const m = await adminFetch(`/analytics/top-markets?days=${topMarketsDays}&limit=5`, password);
      const nc = await adminFetch(`/analytics/nav-clicks?days=${navClicksDays}&limit=15`, password);
      setSummary(s);
      setContacts(c.rows || []);
      setTopMarkets(m);
      setNavClicks(nc);
      setAuthed(true);
      // Only persist when the legacy header succeeded — empty-password login
      // means we're trusting the JWT cookie and there's no password to cache.
      if (password) localStorage.setItem(ADMIN_PW_KEY, password);
      void loadFx(false);
    } catch (e) {
      // Silent on cookie-only attempts so the password gate still renders
      // cleanly when no auth at all is present.
      if (password) setError('Invalid password');
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  // Reload top markets whenever the time window changes (after auth).
  useEffect(() => {
    if (!authed) return;
    const password = localStorage.getItem(ADMIN_PW_KEY);
    if (!password) return;
    (async () => {
      try {
        const m = await adminFetch(`/analytics/top-markets?days=${topMarketsDays}&limit=5`, password);
        setTopMarkets(m);
      } catch (_e) { /* ignore */ }
    })();
  }, [topMarketsDays, authed]);

  // Reload nav-click leaderboard when its window changes.
  useEffect(() => {
    if (!authed) return;
    const password = localStorage.getItem(ADMIN_PW_KEY);
    if (!password) return;
    (async () => {
      try {
        const nc = await adminFetch(`/analytics/nav-clicks?days=${navClicksDays}&limit=15`, password);
        setNavClicks(nc);
      } catch (_e) { /* ignore */ }
    })();
  }, [navClicksDays, authed]);

  const loadRevenue = useCallback(async () => {
    const password = localStorage.getItem(ADMIN_PW_KEY);
    if (!password) return;
    const dayParam = revenueDays > 0 ? `&days=${revenueDays}` : '';
    try {
      const [sum, tx] = await Promise.all([
        adminFetch(`/admin/revenue/summary${revenueDays > 0 ? `?days=${revenueDays}` : ''}`, password),
        adminFetch(`/admin/transactions?limit=50${txStatus ? `&status=${txStatus}` : ''}${dayParam}`, password),
      ]);
      setRevenue(sum);
      setTransactions(tx.rows || []);
    } catch (_e) { /* ignore */ }
  }, [txStatus, revenueDays]);

  const loadLeads = useCallback(async () => {
    const password = localStorage.getItem(ADMIN_PW_KEY);
    if (!password) return;
    try {
      const data = await adminFetch(`/admin/systems-leads?limit=100${leadKind ? `&kind=${leadKind}` : ''}`, password);
      setLeads(data.rows || []);
      setLeadCounts(data.counts_by_kind || {});
    } catch (_e) { /* ignore */ }
  }, [leadKind]);

  // Auto-load tab data when switching in
  useEffect(() => {
    if (!authed) return;
    if (tab === 'revenue') void loadRevenue();
    if (tab === 'leads') void loadLeads();
  }, [tab, authed, loadRevenue, loadLeads]);

  const loadFx = async (forceRefresh) => {
    setFxLoading(true);
    try {
      const res = await fetch(`${API}/fx/rates${forceRefresh ? '?refresh=true' : ''}`);
      if (res.ok) setFx(await res.json());
    } catch (_e) {
      /* ignore */
    } finally {
      setFxLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    void attemptLogin(pw);
  };

  const refresh = () => void attemptLogin(pw);

  const logout = () => {
    localStorage.removeItem(ADMIN_PW_KEY);
    setPw('');
    setAuthed(false);
    setSummary(null);
    setContacts([]);
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7] px-4">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-8 shadow-xl"
          data-testid="admin-login-form"
        >
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#fef3e2]">
              <Lock className="h-6 w-6 text-[#f5a623]" strokeWidth={2.2} />
            </div>
            <h1 className="text-xl font-bold text-gray-900">ZHELTA Admin</h1>
            <p className="text-sm text-gray-500">Enter your password to continue</p>
          </div>
          <input
            type="password"
            autoFocus
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            data-testid="admin-password-input"
            placeholder="Admin password"
            className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm focus:border-[#f5a623] focus:outline-none"
          />
          {error && (
            <p className="mt-3 text-sm text-red-600" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading || !pw}
            data-testid="admin-login-submit"
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition hover:brightness-105 disabled:opacity-60"
            style={{ background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)' }}
          >
            <LogIn className="h-4 w-4" />
            {loading ? 'Checking\u2026' : 'Sign in'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <header className="bg-[#0e0e10] text-white">
        <div className="zh-container flex items-center justify-between py-4">
          <h1 className="text-lg font-bold tracking-wide">ZHELTA Admin Dashboard</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={refresh}
              disabled={loading}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold hover:bg-white/15 transition"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="text-xs font-semibold text-white/70 hover:text-white transition"
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      {/* Tabs — gated by useAdminScope. Division admins only see their own
          tabs; super-admin sees everything. Backend mirrors these rules. */}
      <SecurityBadge />
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.02)]">
        <div className="zh-container flex items-center gap-1 overflow-x-auto" data-testid="admin-tabs">
          {scope.allowedTabs.has('overview') && <TabBtn id="overview" active={tab} onClick={setTab} icon={BarChart3} label="Overview" />}
          {scope.allowedTabs.has('revenue')  && <TabBtn id="revenue"  active={tab} onClick={setTab} icon={CreditCard} label="Revenue" />}
          {scope.allowedTabs.has('leads')    && <TabBtn id="leads"    active={tab} onClick={setTab} icon={Inbox} label="Systems Leads" badge={Object.values(leadCounts).reduce((a,b)=>a+b,0) || null} />}
          {scope.allowedTabs.has('services') && <TabBtn id="services" active={tab} onClick={setTab} icon={ShoppingBag} label="Services" />}
          {scope.allowedTabs.has('users')    && <TabBtn id="users"    active={tab} onClick={setTab} icon={Users} label="Users" />}
          {scope.allowedTabs.has('catalog')  && <TabBtn id="catalog"  active={tab} onClick={setTab} icon={Boxes} label="Catalog" />}
          {scope.allowedTabs.has('academy')  && <TabBtn id="academy"  active={tab} onClick={setTab} icon={GraduationCap} label="Academy" />}
          {scope.allowedTabs.has('discounts')&& <TabBtn id="discounts" active={tab} onClick={setTab} icon={KeyRound} label="Discounts" />}
          {scope.canManageUsers              && <TabBtn id="team"     active={tab} onClick={setTab} icon={ShieldCheck} label="Admin Team" />}
          {/* Role badge on the right edge of the tab bar */}
          <div className="ml-auto pr-2 flex items-center gap-2 shrink-0" data-testid="admin-scope-badge">
            <span className={`text-[9.5px] font-extrabold uppercase tracking-[0.18em] px-2 py-1 rounded-full ${
              scope.role === 'super_admin' ? 'bg-zinc-950 text-[#f5a623]'
              : scope.role === 'systems_admin' ? 'bg-orange-100 text-orange-800'
              : scope.role === 'services_admin' ? 'bg-amber-100 text-amber-800'
              : scope.role === 'academy_admin' ? 'bg-purple-100 text-purple-800'
              : 'bg-gray-100 text-gray-700'
            }`}>
              {scope.displayName}
            </span>
          </div>
        </div>
      </div>

      <main className="zh-container py-10 space-y-8">
        {tab === 'overview' && summary && (
          <div className="flex flex-col gap-4">
            {/* AT A GLANCE — top-line metrics that admins always care about */}
            <CollapsibleSection
              id="ov-at-a-glance"
              eyebrow="At a glance"
              title="Today's pulse"
              icon={Sparkles}
            >
              <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  icon={Users}
                  label="Unique sessions"
                  value={summary.totals.unique_sessions || 0}
                  color="#a855f7"
                  testId="stat-sessions"
                />
                <StatCard
                  icon={MessageSquare}
                  label="Contact submissions"
                  value={summary.totals.contacts}
                  color="#16a34a"
                  testId="stat-contacts"
                />
                <StatCard
                  icon={Globe}
                  label="Total events tracked"
                  value={summary.totals.events}
                  color="#f5a623"
                  testId="stat-events"
                />
                <StatCard
                  icon={Database}
                  label="Translation cache entries"
                  value={summary.totals.cache_entries}
                  color="#3b82f6"
                  testId="stat-cache"
                />
              </section>
              <FxTile fx={fx} loading={fxLoading} onRefresh={() => loadFx(true)} />
            </CollapsibleSection>

            {/* GROWTH — sales, conversions, recovery */}
            <CollapsibleSection
              id="ov-growth"
              eyebrow="Growth · Sales · Recovery"
              title="Conversion & revenue health"
              icon={TrendingUp}
            >
              <CompareConversionWidget />
              <CompareRecoveryWidget />
              <WinningCodesWidget />
              <DiscountFunnelWidget />
            </CollapsibleSection>

            {/* AUDIENCE — where visitors come from, what they do */}
            <CollapsibleSection
              id="ov-audience"
              eyebrow="Audience · Behavior"
              title="Visitor insights"
              icon={Eye}
              defaultOpen={false}
            >
              <CrossDivisionTrafficWidget />
              <TopMarketsWidget
                data={topMarkets}
                days={topMarketsDays}
                onDaysChange={setTopMarketsDays}
              />
              <CrossDivisionPathsWidget />
              <SearchAnalyticsWidget />
              <NavClicksWidget
                data={navClicks}
                days={navClicksDays}
                onDaysChange={setNavClicksDays}
              />
              <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <BreakdownCard
                  icon={Globe}
                  title="Visitors by country"
                  rows={summary.countries}
                  renderLabel={(r) => `${r.code} · ${r.name}`}
                  renderValue={(r) => r.count}
                  empty="No tracked visitors yet. Load the public site once to populate."
                />
                <BreakdownCard
                  icon={Languages}
                  title="Language usage"
                  rows={summary.languages}
                  renderLabel={(r) => r.lang}
                  renderValue={(r) => r.count}
                />
                <BreakdownCard
                  icon={DollarSign}
                  title="Currency usage"
                  rows={summary.currencies}
                  renderLabel={(r) => r.currency}
                  renderValue={(r) => r.count}
                />
                <BreakdownCard
                  icon={Database}
                  title="Translation cache by language"
                  rows={summary.cache_by_lang}
                  renderLabel={(r) => r.lang || 'unknown'}
                  renderValue={(r) => r.count}
                />
              </section>
              {summary.sessions && (
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="session-segmentation">
                  <BreakdownCard
                    icon={Users}
                    title="Unique sessions by region"
                    rows={summary.sessions.by_region}
                    renderLabel={(r) => r.region}
                    renderValue={(r) => r.count}
                    empty="No segmented sessions yet."
                  />
                  <BreakdownCard
                    icon={DollarSign}
                    title="Unique sessions by currency"
                    rows={summary.sessions.by_currency}
                    renderLabel={(r) => r.currency}
                    renderValue={(r) => r.count}
                  />
                  <BreakdownCard
                    icon={Languages}
                    title="Unique sessions by language"
                    rows={summary.sessions.by_lang}
                    renderLabel={(r) => r.lang}
                    renderValue={(r) => r.count}
                  />
                </section>
              )}
            </CollapsibleSection>

            {/* COMMERCE — what's selling */}
            <CollapsibleSection
              id="ov-commerce"
              eyebrow="Commerce · Catalog"
              title="Top products & variants"
              icon={ShoppingBag}
              defaultOpen={false}
            >
              <TopProductsWidget />
              <VariantImageEditor />
            </CollapsibleSection>

            {/* RETENTION & SECURITY */}
            <CollapsibleSection
              id="ov-retention-security"
              eyebrow="Retention · Security"
              title="Sessions, churn & access"
              icon={ShieldCheck}
              defaultOpen={false}
            >
              <ChurnRadarWidget adminPassword={pw || localStorage.getItem(ADMIN_PW_KEY) || ''} />
              <SessionsGeoMap />
              <SecurityDigestCard />
              <CredentialsCheatsheetCard />
            </CollapsibleSection>

            {/* OPS — health, callbacks, recent submissions */}
            <CollapsibleSection
              id="ov-ops"
              eyebrow="Ops · Monitoring"
              title="System health & inbox"
              icon={Activity}
              defaultOpen={false}
            >
              <CronHealthWidget />
              <CallbacksWidget />
              <section className="rounded-2xl border border-gray-200 bg-white p-6">
                <h3 className="mb-4 text-base font-bold text-gray-900">Recent contact submissions</h3>
                {contacts.length === 0 ? (
                  <p className="text-sm text-gray-500">No contact submissions yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-100">
                          <th className="py-2 pr-4 font-semibold">When</th>
                          <th className="py-2 pr-4 font-semibold">Name</th>
                          <th className="py-2 pr-4 font-semibold">Email</th>
                          <th className="py-2 pr-4 font-semibold">Subject</th>
                          <th className="py-2 pr-4 font-semibold">Lang</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((c) => (
                          <tr key={c.id} className="border-b border-gray-50">
                            <td className="py-2 pr-4 text-gray-600 whitespace-nowrap">
                              {new Date(c.created_at).toLocaleString()}
                            </td>
                            <td className="py-2 pr-4 text-gray-900 font-medium">{c.name}</td>
                            <td className="py-2 pr-4 text-gray-600">{c.email}</td>
                            <td className="py-2 pr-4 text-gray-700">{c.subject}</td>
                            <td className="py-2 pr-4">
                              <span className="inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-[#c9a227] uppercase">
                                {c.lang || 'en'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </CollapsibleSection>
          </div>
        )}

        {tab === 'revenue' && (
          <div className="flex flex-col gap-4">
            <CollapsibleSection
              id="rev-unified"
              eyebrow="Revenue · All divisions"
              title="Unified revenue snapshot"
              icon={DollarSign}
            >
              <UnifiedRevenueWidget />
            </CollapsibleSection>
            <CollapsibleSection
              id="rev-transactions"
              eyebrow="Transactions · Stripe"
              title="Recent payments & status"
              icon={CreditCard}
              defaultOpen={false}
            >
              <RevenueTab
                revenue={revenue}
                transactions={transactions}
                txStatus={txStatus}
                setTxStatus={setTxStatus}
                days={revenueDays}
                setDays={setRevenueDays}
              />
            </CollapsibleSection>
          </div>
        )}

        {tab === 'leads' && (
          <LeadsTab leads={leads} counts={leadCounts} activeKind={leadKind} setKind={setLeadKind} />
        )}

        {tab === 'services' && (
          <ServicesTab />
        )}

        {tab === 'users' && (
          <UsersTab adminPassword={pw || localStorage.getItem(ADMIN_PW_KEY) || ''} />
        )}

        {tab === 'catalog' && (
          <CatalogTab adminPassword={pw || localStorage.getItem(ADMIN_PW_KEY) || ''} />
        )}

        {tab === 'academy' && (
          <AcademyAdminPanel adminPassword={pw || localStorage.getItem(ADMIN_PW_KEY) || ''} />
        )}

        {tab === 'team' && scope.canManageUsers && (
          <AdminTeamPanel scope={scope} />
        )}

        {tab === 'discounts' && (
          <DiscountsPanel />
        )}
      </main>
    </div>
  );
function WinningCodesWidget() {
  const [rows, setRows] = useState(null);
  const [busyCode, setBusyCode] = useState(null);

  const reload = async () => {
    try {
      const res = await fetch(`${API}/academy/admin/discounts/winners`, { credentials: 'include' });
      if (!res.ok) { setRows([]); return; }
      const data = await res.json();
      setRows(data.rows || []);
    } catch { setRows([]); }
  };
  useEffect(() => { reload(); }, []);

  const promote = async (code, channel) => {
    if (!window.confirm(`Send the "${code}" upsell via ${channel}? This will email every Standard-tier student in the targeted programs and/or fire a Teams card.`)) return;
    setBusyCode(code);
    try {
      const res = await fetch(`${API}/academy/admin/discounts/${code}/promote?channel=${channel}`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      alert(`✅ Promoted ${code}.\nAudience: ${data.audience} students\nEmails sent: ${data.sent}\nChannel: ${data.channel}`);
    } catch (e) {
      alert(e.message);
    } finally { setBusyCode(null); }
  };

  if (rows === null || rows.length === 0) return null;

  return (
    <section className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-amber-50 p-5"
      data-testid="winning-codes">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-[12px] uppercase tracking-[0.18em] text-emerald-700 font-bold">🔥 Promote winning code{rows.length === 1 ? '' : 's'}</div>
          <div className="text-[14px] text-[#1D1D1F]">{rows.length} code{rows.length === 1 ? ' is' : 's are'} converting at ≥40% — push to existing students for upsell.</div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {rows.map((r) => (
          <div key={r.code} className="rounded-xl border border-[#E5E5E7] bg-white p-4" data-testid={`winning-card-${r.code}`}>
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="min-w-0">
                <div className="font-mono font-extrabold text-[#1D1D1F]">{r.code}</div>
                <div className="text-[11.5px] text-[#6E6E73]">{r.percent}% off · {r.viewed_7d} views (7d) · <span className="text-emerald-700 font-bold">{r.conversion_pct}% conv</span></div>
              </div>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200">
                ✓ Winner
              </span>
            </div>
            <div className="text-[12.5px] text-[#1D1D1F] mb-3">
              <strong>{r.audience_count}</strong> Standard-tier student{r.audience_count === 1 ? '' : 's'} could be invited to upgrade.
              {r.audience_preview?.length > 0 && (
                <div className="text-[11px] text-[#6E6E73] mt-1 truncate">
                  {r.audience_preview.map((a) => a.email).join(', ')}{r.audience_count > r.audience_preview.length ? ` +${r.audience_count - r.audience_preview.length} more` : ''}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => promote(r.code, 'email')} disabled={busyCode === r.code || r.audience_count === 0}
                data-testid={`promote-${r.code}-email`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-50"
                style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
                📧 Email
              </button>
              <button onClick={() => promote(r.code, 'teams')} disabled={busyCode === r.code}
                data-testid={`promote-${r.code}-teams`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider border border-[#0e0e10] text-[#0e0e10] hover:bg-[#0e0e10] hover:text-white transition-colors disabled:opacity-50">
                📣 Teams card
              </button>
              <button onClick={() => promote(r.code, 'both')} disabled={busyCode === r.code || r.audience_count === 0}
                data-testid={`promote-${r.code}-both`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider text-white disabled:opacity-50"
                style={{ background: '#0e0e10' }}>
                🚀 Email + Teams
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}


}

function DiscountFunnelWidget() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  const reload = async () => {
    setError('');
    try {
      // JWT-cookie protected endpoint — admin must have logged in via /academy/login
      const res = await fetch(`${API}/academy/admin/discounts/funnel`, { credentials: 'include' });
      if (!res.ok) throw new Error(res.status === 401 ? 'Sign in via /academy/login first' : `HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.rows || []);
    } catch (e) { setError(e.message); }
  };
  useEffect(() => { reload(); }, []);

  if (error) {
    return (
      <section className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5">
        <div className="text-[12px] uppercase tracking-[0.18em] text-[#a8871a] font-bold mb-1">Discount conversion funnel</div>
        <div className="text-[13px] text-[#6E6E73]">{error}</div>
      </section>
    );
  }
  if (rows === null) {
    return (
      <section className="rounded-2xl border border-[#E5E5E7] bg-white p-5">
        <div className="text-[13px] text-[#6E6E73]">Loading discount funnel…</div>
      </section>
    );
  }
  if (rows.length === 0) {
    return (
      <section className="rounded-2xl border border-[#E5E5E7] bg-white p-5">
        <div className="text-[12px] uppercase tracking-[0.18em] text-[#a8871a] font-bold mb-1">Discount conversion funnel</div>
        <div className="text-[13px] text-[#6E6E73]">No discount codes yet — create one in Academy → Discounts to start tracking conversion.</div>
      </section>
    );
  }

  const totalRevenue = rows.reduce((a, r) => a + (r.revenue || 0), 0);
  const totalPaid = rows.reduce((a, r) => a + (r.checkout_paid || 0), 0);

  return (
    <section className="rounded-2xl border border-[#E5E5E7] bg-white p-5" data-testid="discount-funnel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[12px] uppercase tracking-[0.18em] text-[#a8871a] font-bold">Discount conversion funnel</div>
          <div className="text-[14px] font-extrabold">{rows.length} code{rows.length === 1 ? '' : 's'} · ${totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })} revenue · {totalPaid} sale{totalPaid === 1 ? '' : 's'}</div>
        </div>
        <button onClick={reload} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] border border-[#0e0e10] hover:bg-[#0e0e10] hover:text-white transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-[#E5E5E7]">
        <table className="w-full text-[12.5px]">
          <thead className="bg-[#FAFAFA] text-[#6E6E73]">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] uppercase">Code</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase">% off</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase" title="Link visited (?promo=)">Viewed</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase" title="Validate API hit">Previewed</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase">Started</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase">Paid</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase">Revenue</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase">Conv</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E7]">
            {rows.map((r) => {
              const dead = !r.viewed && !r.previewed && !r.checkout_started;
              return (
                <tr key={r.code} className={dead ? 'opacity-60' : ''} data-testid={`funnel-row-${r.code}`}>
                  <td className="px-3 py-2 font-mono font-bold text-[#1D1D1F]">
                    {r.code}{r.one_shot ? <span className="ml-1 text-[10px] text-[#a8871a]">1-shot</span> : null}
                    {!r.active && <span className="ml-1 text-[10px] text-gray-500">(disabled)</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-[#a8871a] font-bold">{r.percent}%</td>
                  <td className="px-3 py-2 text-right">{r.viewed}</td>
                  <td className="px-3 py-2 text-right">{r.previewed}</td>
                  <td className="px-3 py-2 text-right">{r.checkout_started}</td>
                  <td className="px-3 py-2 text-right font-bold text-emerald-700">{r.checkout_paid}</td>
                  <td className="px-3 py-2 text-right font-bold">${(r.revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-bold border
                      ${r.conversion_pct >= 30 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        r.conversion_pct >= 10 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-gray-50 text-gray-600 border-gray-200'}`}>
                      {r.conversion_pct}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-[#6E6E73]">
        Conversion = Paid ÷ Viewed. Greyed-out rows have zero engagement — consider stopping promotion of those codes.
      </p>
    </section>
  );
}


function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div
      className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
      data-testid={testId}
    >
      <div className="mb-3 flex items-center gap-2 text-sm text-gray-500">
        <Icon className="h-4 w-4" style={{ color }} strokeWidth={2.2} />
        {label}
      </div>
      <p className="text-3xl font-extrabold text-gray-900">{value.toLocaleString()}</p>
    </div>
  );
}

function BreakdownCard({ icon: Icon, title, rows, renderLabel, renderValue, empty }) {
  const max = rows.reduce((m, r) => Math.max(m, renderValue(r)), 1);
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-4 w-4 text-gray-500" strokeWidth={2} />
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">{empty || 'No data yet.'}</p>
      ) : (
        <ul className="space-y-2.5">
          {rows.slice(0, 10).map((r, i) => {
            const v = renderValue(r);
            const pct = (v / max) * 100;
            return (
              <li key={i} className="relative">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-800 font-medium">{renderLabel(r)}</span>
                  <span className="text-gray-500">{v}</span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${pct}%`,
                      background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 100%)',
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


function FxTile({ fx, loading, onRefresh }) {
  const rates = fx?.rates || {};
  const codes = Object.keys(rates);
  const updatedStr = fx?.time_last_update_utc || fx?.fetched_at;
  let updatedLabel = '—';
  try {
    if (updatedStr) {
      const d = new Date(updatedStr);
      if (!Number.isNaN(d.getTime())) updatedLabel = d.toLocaleString();
    }
  } catch (_e) { /* noop */ }

  return (
    <section
      className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
      data-testid="admin-fx-tile"
    >
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#f5a623]" strokeWidth={2.2} />
          <h3 className="text-base font-bold text-gray-900">Live exchange rates (USD base)</h3>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          data-testid="admin-fx-refresh"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#0e0e10] text-white px-4 py-1.5 text-xs font-semibold hover:brightness-110 transition disabled:opacity-60"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Force refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500 mb-4">
        <span>Provider: <span className="font-semibold text-gray-700" data-testid="admin-fx-provider">{fx?.provider || '—'}</span></span>
        <span>Last update: <span className="font-semibold text-gray-700" data-testid="admin-fx-updated">{updatedLabel}</span></span>
      </div>

      {codes.length === 0 ? (
        <p className="text-sm text-gray-500">No FX data loaded yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {codes.map((code) => (
            <div
              key={code}
              className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2"
              data-testid={`admin-fx-rate-${code}`}
            >
              <div className="text-[11px] font-semibold text-gray-500">{code}</div>
              <div className="text-sm font-bold text-gray-900">{Number(rates[code]).toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function TopMarketsWidget({ data, days, onDaysChange }) {
  const rows = data?.rows || [];
  const maxSessions = Math.max(1, ...rows.map((r) => r.sessions || 0));
  const windowOptions = [7, 14, 30];

  return (
    <section
      className="rounded-2xl border border-gray-200 p-6 shadow-sm text-white"
      style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #1a1a1f 60%, #2a1e0a 100%)' }}
      data-testid="top-markets-widget"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5 text-[#f5a623]" strokeWidth={2.2} />
          <div>
            <h3 className="text-base font-bold">
              Top 5 markets {days === 7 ? 'this week' : `(last ${days} days)`}
            </h3>
            <p className="text-[12px] text-white/60">Unique sessions by country</p>
          </div>
        </div>
        <div className="inline-flex rounded-full bg-white/10 p-0.5" data-testid="top-markets-window-switch">
          {windowOptions.map((d) => (
            <button
              key={d}
              onClick={() => onDaysChange(d)}
              data-testid={`top-markets-window-${d}`}
              className={`px-3 py-1 text-[12px] font-semibold rounded-full transition ${
                days === d ? 'bg-[#f5a623] text-black' : 'text-white/80 hover:text-white'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-white/60">No tracked traffic in this window yet.</p>
      ) : (
        <ol className="space-y-3">
          {rows.map((r, i) => {
            const pct = ((r.sessions || 0) / maxSessions) * 100;
            return (
              <li key={`${i}-${r.code}`} data-testid={`top-market-${i + 1}`} className="relative">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="flex items-center gap-2.5">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#f5a623] text-black text-[11px] font-extrabold">
                      {i + 1}
                    </span>
                    <span className="font-semibold tracking-wide">{r.name}</span>
                    <span className="text-[11px] text-white/50 uppercase">{r.code}</span>
                  </span>
                  <span className="text-white/80 tabular-nums">
                    {r.sessions} session{r.sessions === 1 ? '' : 's'}
                    <span className="ml-2 text-white/40 text-[11px]">{r.events} events</span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(6, pct)}%`,
                      background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 100%)',
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}


function NavClicksWidget({ data, days, onDaysChange }) {
  const allRows = data?.rows || [];
  const [scope, setScope] = useState('all');
  const windowOptions = [7, 30, 90];
  const scopes = [
    { key: 'all',     label: 'All',     match: () => true },
    { key: 'main',    label: 'Main',    match: (s) => s.startsWith('main:') },
    { key: 'systems', label: 'Systems', match: (s) => s.startsWith('systems:') },
    { key: 'academy', label: 'Academy', match: (s) => s.startsWith('academy:') },
  ];
  const matcher = scopes.find((s) => s.key === scope) || scopes[0];

  // Filter + recompute totals so the bar widths and counts adapt to the chip.
  const rows = allRows.filter((r) => matcher.match(r.slug || ''));
  const total = rows.reduce((sum, r) => sum + (r.clicks || 0), 0);
  const max = Math.max(1, ...rows.map((r) => r.clicks || 0));

  // Pretty-print the namespaced slug into a breadcrumb.
  const prettify = (slug) => {
    if (!slug) return '';
    const parts = slug.split(':');
    // Drop the leading scope for readability when filtered to a single scope.
    if (scope !== 'all' && parts[0] === scope) return parts.slice(1).join(' › ');
    return parts.join(' › ');
  };

  return (
    <section
      className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
      data-testid="nav-clicks-widget"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <MousePointerClick className="h-5 w-5 text-[#c9a227]" strokeWidth={2.2} />
          <div>
            <h3 className="text-base font-bold text-gray-900">
              Top nav clicks {days === 7 ? 'this week' : `(last ${days} days)`}
            </h3>
            <p className="text-[12px] text-gray-500">
              {matcher.key === 'all'
                ? 'Mega-menu items, featured CTAs & header buttons'
                : `${matcher.label} surface only`}
              {' — '}
              {total} total click{total === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <div className="inline-flex rounded-full bg-gray-100 p-0.5" data-testid="nav-clicks-window-switch">
          {windowOptions.map((d) => (
            <button
              key={d}
              onClick={() => onDaysChange(d)}
              data-testid={`nav-clicks-window-${d}`}
              className={`px-3 py-1 text-[12px] font-semibold rounded-full transition ${
                days === d ? 'bg-[#c9a227] text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Surface-scope filter chips */}
      <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="nav-clicks-scope-chips">
        {scopes.map((s) => {
          const count = allRows.filter((r) => s.match(r.slug || '')).length;
          const active = scope === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              data-testid={`nav-clicks-scope-${s.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold border transition-colors ${
                active
                  ? 'bg-[#c9a227] border-[#c9a227] text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-[#c9a227]/60 hover:text-[#c9a227]'
              }`}
            >
              {s.label}
              <span
                className={`inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                  active ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">
          {allRows.length === 0
            ? 'No nav clicks tracked yet in this window. Click a Systems or Academy header item to light this up.'
            : `No ${matcher.label} clicks in this window — try a different surface or widen the time range.`}
        </p>
      ) : (
        <ol className="space-y-3">
          {rows.map((r, i) => {
            const pct = ((r.clicks || 0) / max) * 100;
            return (
              <li key={r.slug} data-testid={`nav-click-row-${i + 1}`} className="relative">
                <div className="flex items-center justify-between text-sm mb-1 gap-3">
                  <span className="flex items-center gap-2.5 min-w-0">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#c9a227] text-white text-[11px] font-extrabold shrink-0">
                      {i + 1}
                    </span>
                    <span className="font-semibold text-gray-900 truncate">{r.label}</span>
                  </span>
                  <span className="text-gray-700 tabular-nums whitespace-nowrap">
                    {r.clicks} click{r.clicks === 1 ? '' : 's'}
                    <span className="ml-2 text-gray-400 text-[11px]">{r.sessions} session{r.sessions === 1 ? '' : 's'}</span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mb-1">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(6, pct)}%`,
                      background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 100%)',
                    }}
                  />
                </div>
                <div className="text-[11px] text-gray-400 font-mono truncate">{prettify(r.slug)}</div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}


function TabBtn({ id, active, onClick, icon: Icon, label, badge }) {
  const isActive = active === id;
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`admin-tab-${id}`}
      className={`relative inline-flex items-center gap-2 px-4 py-3 text-[13px] font-semibold transition-colors ${isActive ? 'text-[#f5a623]' : 'text-gray-600 hover:text-gray-900'}`}
    >
      <Icon className="h-4 w-4" strokeWidth={2.2} />
      <span>{label}</span>
      {badge ? <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-[10px] font-bold text-[#c9a227]">{badge}</span> : null}
      {isActive && <span className="absolute left-3 right-3 bottom-0 h-0.5 rounded-full bg-[#f5a623]" />}
    </button>
  );
}

function formatMoney(n) {
  if (n === null || n === undefined) return '$0';
  return '$' + Math.round(n).toLocaleString();
}

async function triggerCsvDownload(path, filename) {
  const password = localStorage.getItem(ADMIN_PW_KEY);
  if (!password) return;
  try {
    const res = await fetch(`${API}${path}`, { headers: { 'X-Admin-Password': password } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (_e) { /* ignore */ }
}

function RevenueTab({ revenue, transactions, txStatus, setTxStatus, days, setDays }) {
  const exportQuery = `${txStatus ? `?status=${txStatus}` : '?'}${days > 0 ? (txStatus ? `&days=${days}` : `days=${days}`) : ''}`;
  if (!revenue) {
    return <div className="py-16 text-center text-gray-500">Loading revenue…</div>;
  }
  const t = revenue.totals || {};
  const cards = [
    { icon: CreditCard, label: 'Total revenue (paid)', value: formatMoney(t.total_revenue), color: '#16a34a', testId: 'rev-total' },
    { icon: TrendingUp, label: 'MRR (subscriptions)', value: formatMoney(t.mrr), color: '#f5a623', testId: 'rev-mrr' },
    { icon: DollarSign, label: 'Avg order value', value: formatMoney(t.aov), color: '#3b82f6', testId: 'rev-aov' },
    { icon: Award,      label: 'Conversion rate', value: `${t.conversion_pct || 0}%`, color: '#a855f7', testId: 'rev-conv' },
  ];
  const dayOptions = [
    { val: 0,  lbl: 'All time' },
    { val: 7,  lbl: '7d' },
    { val: 30, lbl: '30d' },
    { val: 90, lbl: '90d' },
  ];
  return (
    <div className="space-y-8" data-testid="admin-revenue-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex rounded-full bg-gray-100 p-0.5" data-testid="rev-range">
          {dayOptions.map((o) => (
            <button
              key={o.val}
              onClick={() => setDays(o.val)}
              data-testid={`rev-range-${o.val}`}
              className={`px-3 py-1.5 text-[12px] font-semibold rounded-full transition ${days === o.val ? 'bg-black text-white' : 'text-gray-700 hover:text-gray-900'}`}
            >
              {o.lbl}
            </button>
          ))}
        </div>
        <button
          onClick={() => triggerCsvDownload(`/admin/transactions/export${exportQuery}`.replace('?&','?').replace(/\?$/, ''), `zhelta-transactions-${new Date().toISOString().slice(0,10)}.csv`)}
          data-testid="rev-export-csv"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#f5a623] text-black px-4 py-1.5 text-xs font-bold hover:brightness-110 transition"
        >
          Export CSV
        </button>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-base font-bold text-gray-900 mb-5">Transactions by status</h3>
          <ul className="space-y-2.5">
            {[
              { label: 'Paid',    count: t.paid_count,    color: '#16a34a' },
              { label: 'Pending', count: t.pending_count, color: '#f5a623' },
              { label: 'Expired', count: t.expired_count, color: '#9ca3af' },
            ].map((r) => (
              <li key={r.label} className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
                <span className="text-[14px] text-gray-700 flex-1">{r.label}</span>
                <span className="text-sm font-bold text-gray-900 tabular-nums">{r.count || 0}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 pt-5 border-t border-gray-100 text-[12px] text-gray-500">
            {t.total_tx || 0} total transactions
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-base font-bold text-gray-900 mb-5">Top 5 tiers by sales</h3>
          {(!revenue.top_tiers || revenue.top_tiers.length === 0) ? (
            <p className="text-sm text-gray-500">No paid orders yet.</p>
          ) : (
            <ul className="space-y-3">
              {revenue.top_tiers.map((r, i) => (
                <li key={r.slug + r.tier_id} className="flex items-center gap-3" data-testid={`top-tier-${i + 1}`}>
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#f5a623] text-black text-[11px] font-extrabold flex-shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-gray-900 truncate">{r.service_name}</div>
                    <div className="text-[11px] text-gray-500 truncate">{r.tier_name} · {r.count} sold</div>
                  </div>
                  <div className="text-sm font-bold text-gray-900 tabular-nums">{formatMoney(r.revenue)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
          <h3 className="text-base font-bold text-gray-900">Recent transactions</h3>
          <div className="inline-flex rounded-full bg-gray-100 p-0.5" data-testid="tx-filter">
            {[['', 'All'], ['paid', 'Paid'], ['pending', 'Pending'], ['expired', 'Expired']].map(([val, lbl]) => (
              <button
                key={val}
                onClick={() => setTxStatus(val)}
                data-testid={`tx-filter-${val || 'all'}`}
                className={`px-3 py-1 text-[12px] font-semibold rounded-full transition ${txStatus === val ? 'bg-black text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
        {(!transactions || transactions.length === 0) ? (
          <p className="text-sm text-gray-500">No transactions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] font-extrabold uppercase tracking-[0.16em] text-gray-500 border-b border-gray-100">
                <tr>
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Service</th>
                  <th className="py-2 pr-4">Tier</th>
                  <th className="py-2 pr-4">Amount</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.session_id} className="border-b border-gray-50" data-testid={`tx-row-${tx.session_id}`}>
                    <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">{(tx.created_at || '').slice(0, 10)}</td>
                    <td className="py-2 pr-4 text-gray-800 font-medium">{tx.service_name}</td>
                    <td className="py-2 pr-4 text-gray-600">{tx.tier_name}</td>
                    <td className="py-2 pr-4 font-semibold text-gray-900">{formatMoney(tx.amount)}</td>
                    <td className="py-2 pr-4 text-gray-600">{tx.email || '—'}</td>
                    <td className="py-2 pr-4">
                      <StatusPill payment={tx.payment_status} status={tx.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatusPill({ payment, status }) {
  const label = status === 'expired' ? 'Expired' : (payment || 'pending');
  const color = status === 'expired'
    ? 'bg-gray-100 text-gray-600'
    : payment === 'paid' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
    : 'bg-amber-50 text-amber-700 border border-amber-100';
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${color}`}>{label}</span>;
}

function LeadsTab({ leads, counts, activeKind, setKind }) {
  const chips = [
    ['', 'All', (counts && Object.values(counts).reduce((a,b)=>a+b,0)) || 0],
    ['get-started', 'Get started', counts['get-started'] || 0],
    ['book-demo', 'Demo', counts['book-demo'] || 0],
    ['consultation', 'Consultation', counts['consultation'] || 0],
    ['scheduled', 'Scheduled', counts['scheduled'] || 0],
    ['contact', 'Contact', counts['contact'] || 0],
    ['custom-build', 'Custom build', counts['custom-build'] || 0],
  ];
  const exportQs = activeKind ? `?kind=${activeKind}` : '';
  return (
    <div className="space-y-6" data-testid="admin-leads-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex flex-wrap gap-1 rounded-full bg-gray-100 p-0.5" data-testid="leads-filter">
          {chips.map(([val, lbl, n]) => (
            <button
              key={val || 'all'}
              onClick={() => setKind(val)}
              data-testid={`leads-filter-${val || 'all'}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-full transition ${activeKind === val ? 'bg-black text-white' : 'text-gray-700 hover:text-gray-900'}`}
            >
              {lbl} <span className={`text-[10px] ${activeKind === val ? 'text-white/70' : 'text-gray-500'}`}>{n}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[12px] text-gray-500">Showing {leads.length} lead{leads.length === 1 ? '' : 's'}</span>
          <button
            onClick={() => triggerCsvDownload(`/admin/systems-leads/export${exportQs}`, `zhelta-leads-${new Date().toISOString().slice(0,10)}.csv`)}
            data-testid="leads-export-csv"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#f5a623] text-black px-4 py-1.5 text-xs font-bold hover:brightness-110 transition"
          >
            Export CSV
          </button>
        </div>
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        {(!leads || leads.length === 0) ? (
          <p className="text-sm text-gray-500">No leads in this segment yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {leads.map((l) => (
              <li key={l.id} className="py-4 flex items-start gap-4" data-testid={`lead-${l.id}`}>
                <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center text-[#c9a227] font-bold text-sm flex-shrink-0">
                  {(l.name || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[14px] font-bold text-gray-900">{l.name}</span>
                    <span className="text-[12px] text-gray-500">{l.email}</span>
                    {l.company && <span className="text-[11px] text-gray-500">· {l.company}</span>}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    <span className="inline-block rounded-full bg-amber-100 text-[#c9a227] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">{l.kind}</span>
                    {l.slug && <span className="inline-block rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-[10px] font-semibold">{l.slug}</span>}
                    {l.tier_id && <span className="inline-block rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-[10px] font-semibold">{l.tier_id}</span>}
                    {(l.recommended_slug || l.recommended_tier) && (
                      <a
                        href={l.recommended_slug && l.recommended_tier
                          ? `/systems/solutions/${l.recommended_slug}/pricing?highlight=${l.recommended_tier}`
                          : '/systems/pricing'}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`lead-${l.id}-matched-tier`}
                        title="Open the recommended pricing page in a new tab"
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#0e0e10] hover:brightness-105"
                        style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}
                      >
                        ★ Matched: {l.recommended_slug || '—'} · {l.recommended_tier || '—'}
                      </a>
                    )}
                  </div>
                  <p className="mt-2 text-[13px] text-gray-700 leading-relaxed line-clamp-3 whitespace-pre-wrap">{l.message}</p>
                  {(l.phone || l.preferred_time || l.budget || l.timeline || l.scheduled_at) && (
                    <div className="mt-2 text-[11px] text-gray-500 space-x-3">
                      {l.scheduled_at && <span>🗓 {new Date(l.scheduled_at).toLocaleString()}</span>}
                      {l.phone && <span>📞 {l.phone}</span>}
                      {l.preferred_time && <span>🕒 {l.preferred_time}</span>}
                      {l.budget && <span>💰 {l.budget}</span>}
                      {l.timeline && <span>⏱ {l.timeline}</span>}
                    </div>
                  )}
                </div>
                <span className="text-[11px] text-gray-400 flex-shrink-0 whitespace-nowrap">{(l.created_at || '').slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function SecurityBadge() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  // null | 'setup' | 'disable' | 'regenerate' | { stage: 'show', codes: [...] }
  const [modal, setModal] = useState(null);

  const reload = useCallback(() => {
    fetch(`${API}/academy/admin/security/me`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setData(d))
      .catch(() => {});
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const signOutEverywhere = async () => {
    if (!window.confirm('Sign out of every device + browser where this admin is logged in? Anyone currently using this account will be forced to log in again.')) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/academy/admin/security/sign-out-everywhere`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      try { localStorage.removeItem('zhelta_admin_pw'); } catch {/* ignore */}
      alert('All admin sessions signed out. Reloading…');
      window.location.href = process.env.REACT_APP_ADMIN_LOGIN_PATH || '/zhelta-internal/console';
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  if (!data || !data.sessions || data.sessions.length === 0) return null;
  const cur = data.sessions[0];
  const prev = data.sessions[1];
  const fmt = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const tfaOn = !!data.totp_enabled;
  const remaining = Number(data.recovery_codes_remaining || 0);
  const lowRemaining = tfaOn && remaining > 0 && remaining < 3;

  return (
    <>
      <section className="bg-[#0e0e10] text-white/80 border-b border-white/5" data-testid="admin-security-badge">
        <div className="zh-container py-2 flex items-center justify-between gap-3 flex-wrap text-[12px]">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-bold uppercase tracking-wider text-[10px]">
              🔒 Secure session
            </span>
            <span
              data-testid="admin-2fa-status"
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider text-[10px] ${
                tfaOn
                  ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                  : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
              }`}
            >
              {tfaOn ? <ShieldCheck className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
              {tfaOn ? '2FA ON' : '2FA OFF'}
            </span>
            {tfaOn && (
              <span data-testid="admin-2fa-recovery-remaining"
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider text-[10px] ${
                  lowRemaining
                    ? 'bg-red-500/15 text-red-300 border-red-500/40'
                    : 'bg-white/5 text-white/60 border-white/15'
                }`}
              >
                <KeyRound className="w-3 h-3" /> {remaining} recovery {remaining === 1 ? 'code' : 'codes'}
              </span>
            )}
            <span className="text-white/60">
              From <span className="text-white font-mono">{cur.ip || '—'}</span>
              <span className="text-white/40"> · </span>
              <span className="text-white">{fmt(cur.ts)}</span>
            </span>
            {prev && (
              <span className="text-white/40 hidden md:inline">
                Previous: {prev.ip || '—'} · {fmt(prev.ts)}
              </span>
            )}
            <span className="text-white/40">· {data.sessions.length} session{data.sessions.length === 1 ? '' : 's'} on record</span>
          </div>
          <div className="flex items-center gap-2">
            {tfaOn ? (
              <>
                <button onClick={() => setModal('regenerate')}
                  data-testid="admin-2fa-regenerate-btn"
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${
                    lowRemaining
                      ? 'bg-red-500/15 text-red-200 border-red-500/40 hover:bg-red-500/25'
                      : 'bg-white/5 text-white/70 border-white/15 hover:bg-white/10'
                  }`}>
                  <RotateCcw className="w-3 h-3" /> {lowRemaining ? 'Regenerate now' : 'Regenerate codes'}
                </button>
                <button onClick={() => setModal('disable')}
                  data-testid="admin-2fa-disable-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-white/5 text-white/70 border border-white/15 hover:bg-white/10">
                  <KeyRound className="w-3 h-3" /> Disable 2FA
                </button>
              </>
            ) : (
              <button onClick={() => setModal('setup')}
                data-testid="admin-2fa-enable-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-200 border border-amber-500/40 hover:bg-amber-500/25">
                <ShieldCheck className="w-3 h-3" /> Enable 2FA
              </button>
            )}
            <button onClick={signOutEverywhere} disabled={busy}
              data-testid="admin-sign-out-everywhere"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 disabled:opacity-50">
              {busy ? 'Signing out…' : 'Sign out everywhere'}
            </button>
          </div>
        </div>
      </section>

      {modal === 'setup' && (
        <TwoFactorSetupModal onClose={() => setModal(null)} onSuccess={() => { setModal(null); reload(); }} />
      )}
      {modal === 'disable' && (
        <TwoFactorDisableModal onClose={() => setModal(null)} onSuccess={() => { setModal(null); reload(); }} />
      )}
      {modal === 'regenerate' && (
        <TwoFactorRegenerateModal
          onClose={() => setModal(null)}
          onCodesIssued={(codes) => setModal({ stage: 'show', codes })}
        />
      )}
      {modal && typeof modal === 'object' && modal.stage === 'show' && (
        <RecoveryCodesPanel
          codes={modal.codes}
          title="New recovery codes"
          subtitle="Save them now"
          onDone={() => { setModal(null); reload(); }}
        />
      )}
    </>
  );
}

function TwoFactorSetupModal({ onClose, onSuccess, forced = false }) {
  const [secret, setSecret] = useState('');
  const [uri, setUri] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // After successful verify, show the 10 recovery codes BEFORE finalising.
  const [recoveryCodes, setRecoveryCodes] = useState(null); // null | string[]
  // StrictMode + dev mode double-invokes effects; without a guard we'd POST
  // /setup twice and the second response would silently overwrite the server's
  // pending secret while the UI keeps the first one rendered → verify fails 401.
  const setupCalledRef = useRef(false);

  // Kick off /setup on mount to generate a fresh secret + QR.
  useEffect(() => {
    if (setupCalledRef.current) return;
    setupCalledRef.current = true;
    setBusy(true);
    fetch(`${API}/academy/admin/security/2fa/setup`, {
      method: 'POST', credentials: 'include',
    })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
        return j;
      })
      .then((j) => { setSecret(j.secret || ''); setUri(j.otpauth_uri || ''); })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  }, []);

  const verify = async (e) => {
    e.preventDefault();
    const trimmed = (code || '').replace(/\s+/g, '');
    if (trimmed.length !== 6) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    setError(''); setBusy(true);
    try {
      const res = await fetch(`${API}/academy/admin/security/2fa/verify`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: trimmed }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
      // Pivot to the recovery-codes screen — admin must save them before finishing.
      setRecoveryCodes(Array.isArray(j.recovery_codes) ? j.recovery_codes : []);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const copySecret = async () => {
    try { await navigator.clipboard.writeText(secret); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* ignore */ }
  };

  // After the codes are shown the admin must explicitly acknowledge they've
  // saved them — this closes the modal AND signals the parent to refresh.
  const finishSetup = () => onSuccess();

  if (recoveryCodes) {
    return <RecoveryCodesPanel codes={recoveryCodes} onDone={finishSetup} title="Save your recovery codes" subtitle="Step 2 of 2" />;
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center px-4 py-8 overflow-auto" data-testid="2fa-setup-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[460px] w-full overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between border-b border-[#E5E5E7]"
             style={{ background: 'linear-gradient(135deg,#c9a227 0%,#a8871a 100%)' }}>
          <div className="flex items-center gap-2 text-white">
            <ShieldCheck className="w-5 h-5" />
            <div>
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/80">{forced ? 'Mandatory · Step 1 of 2' : 'Step 1 of 2'}</div>
              <div className="text-[16px] font-extrabold">Enable two-factor authentication</div>
            </div>
          </div>
          {!forced && (
            <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="2fa-setup-close">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="px-6 py-5 space-y-4">
          {forced && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[12.5px] px-3 py-2" data-testid="2fa-forced-banner">
              For your account's safety, every ZHELTA Admin must enable 2FA before reaching the dashboard. This takes about 60 seconds.
            </div>
          )}
          <p className="text-[13px] text-[#6E6E73] leading-relaxed">
            Scan the QR with <strong>Microsoft Authenticator</strong> or <strong>Google Authenticator</strong>,
            then enter the current 6-digit code below to confirm.
          </p>

          <div className="flex justify-center bg-[#f8f8fa] border border-[#E5E5E7] rounded-xl p-4">
            {busy && !uri && <div className="text-[12px] text-[#6E6E73]">Generating secret…</div>}
            {uri && (
              <QRCodeCanvas value={uri} size={192} bgColor="#f8f8fa" fgColor="#0e0e10" includeMargin={false} data-testid="2fa-qr" />
            )}
          </div>

          {secret && (
            <div className="text-[12px]">
              <div className="text-[#6E6E73] mb-1">Can't scan? Enter this secret manually:</div>
              <button type="button" onClick={copySecret}
                data-testid="2fa-secret-copy"
                className="w-full font-mono text-[13px] tracking-widest bg-[#f8f8fa] border border-[#E5E5E7] rounded-lg py-2 px-3 hover:border-[#c9a227]">
                {secret}
                <span className="ml-2 text-[10px] uppercase tracking-wider text-[#c9a227] font-bold">
                  {copied ? '✓ Copied' : 'Tap to copy'}
                </span>
              </button>
            </div>
          )}

          <form onSubmit={verify} className="space-y-3 pt-2">
            <label className="block text-[11px] font-bold tracking-[0.18em] uppercase text-[#6E6E73]">
              Enter the 6-digit code
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              data-testid="2fa-setup-code"
              className="w-full px-4 py-3 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] focus:ring-2 focus:ring-[#c9a227]/30 outline-none text-center text-[24px] tracking-[0.5em] font-mono"
              placeholder="000000"
              autoFocus
            />

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px] px-3 py-2" data-testid="2fa-setup-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={busy || code.length !== 6}
              data-testid="2fa-setup-verify"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[14px] font-bold uppercase tracking-[0.18em] text-[#0e0e10] disabled:opacity-60"
              style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 50%,#a8871a 100%)' }}
            >
              {busy ? 'Verifying…' : 'Verify & enable'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function RecoveryCodesPanel({ codes, onDone, title, subtitle }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text('ZHELTA Admin — 2FA Recovery Codes', 60, 80);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(80);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 60, 105);
    doc.text(`Account: admin@zhelta.com`, 60, 122);
    doc.setFontSize(10);
    doc.text('Each code can be used ONCE to sign in if you lose access to your authenticator app.', 60, 152);
    doc.text('Store these codes somewhere safe. Treat them like a password.', 60, 167);

    doc.setFont('courier', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0);
    let y = 210;
    codes.forEach((c, i) => {
      const col = i % 2 === 0 ? 80 : 320;
      if (i % 2 === 0 && i > 0) y += 38;
      doc.text(`${String(i + 1).padStart(2, '0')}.  ${c}`, col, y);
    });

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text('Generated by ZHELTA Admin Security · Keep confidential.', 60, 720);
    doc.save('zhelta-2fa-recovery-codes.pdf');
  };

  const downloadTxt = () => {
    const blob = new Blob(
      [
        'ZHELTA Admin — 2FA Recovery Codes\n',
        `Generated: ${new Date().toLocaleString()}\n`,
        `Account: admin@zhelta.com\n\n`,
        'Each code can be used ONCE.\n\n',
        ...codes.map((c, i) => `${String(i + 1).padStart(2, '0')}.  ${c}\n`),
      ],
      { type: 'text/plain' }
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'zhelta-2fa-recovery-codes.txt';
    document.body.appendChild(a); a.click(); a.remove();
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center px-4 py-8 overflow-auto" data-testid="2fa-recovery-panel">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[520px] w-full overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between"
             style={{ background: 'linear-gradient(135deg,#0e0e10 0%,#1a1a1f 100%)' }}>
          <div className="flex items-center gap-2 text-white">
            <KeyRound className="w-5 h-5 text-amber-400" />
            <div>
              {subtitle && <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/60">{subtitle}</div>}
              <div className="text-[16px] font-extrabold">{title}</div>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-[12px] text-amber-900 leading-relaxed">
              Save these codes <strong>now</strong>. Each one works <strong>once</strong> if you lose
              access to your authenticator. They will not be shown again.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 font-mono text-[13px] bg-[#f8f8fa] border border-[#E5E5E7] rounded-xl p-4" data-testid="2fa-recovery-codes-list">
            {codes.map((c, i) => (
              <div key={i} className="flex items-baseline gap-2">
                <span className="text-[#a0a0a8] tabular-nums">{String(i + 1).padStart(2, '0')}.</span>
                <span className="text-[#0e0e10] tracking-wider">{c}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={downloadPdf}
              data-testid="2fa-recovery-download-pdf"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-bold uppercase tracking-wider bg-[#0e0e10] text-white hover:bg-[#1a1a1f]">
              <Download className="w-3.5 h-3.5" /> Download PDF
            </button>
            <button type="button" onClick={downloadTxt}
              data-testid="2fa-recovery-download-txt"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-bold uppercase tracking-wider bg-white border border-[#E5E5E7] text-[#0e0e10] hover:border-[#c9a227]">
              <Download className="w-3.5 h-3.5" /> .txt
            </button>
            <button type="button" onClick={copyAll}
              data-testid="2fa-recovery-copy-all"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-bold uppercase tracking-wider bg-white border border-[#E5E5E7] text-[#0e0e10] hover:border-[#c9a227]">
              {copied ? '✓ Copied' : 'Copy all'}
            </button>
          </div>

          <label className="flex items-start gap-2 pt-2 text-[13px] text-[#0e0e10] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              data-testid="2fa-recovery-acknowledge"
              className="mt-0.5 w-4 h-4 accent-[#c9a227]"
            />
            <span>I have saved these recovery codes in a secure place.</span>
          </label>

          <button type="button" onClick={onDone} disabled={!acknowledged}
            data-testid="2fa-recovery-done"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[14px] font-bold uppercase tracking-[0.18em] text-[#0e0e10] disabled:opacity-50"
            style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 50%,#a8871a 100%)' }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function TwoFactorRegenerateModal({ onClose, onCodesIssued }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = (code || '').replace(/\s+/g, '');
    if (trimmed.length !== 6) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    setError(''); setBusy(true);
    try {
      const res = await fetch(`${API}/academy/admin/security/2fa/recovery-codes/regenerate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: trimmed }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
      onCodesIssued(j.recovery_codes || []);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center px-4 py-8" data-testid="2fa-regenerate-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[420px] w-full overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between bg-[#0e0e10] text-white">
          <div className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-amber-400" />
            <div className="text-[16px] font-extrabold">Regenerate recovery codes</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="2fa-regenerate-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <p className="text-[13px] text-[#6E6E73] leading-relaxed">
            This will <strong>invalidate all 10 existing recovery codes</strong> and issue a fresh set.
            Confirm with your current 6-digit authenticator code.
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            data-testid="2fa-regenerate-code"
            className="w-full px-4 py-3 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] focus:ring-2 focus:ring-[#c9a227]/30 outline-none text-center text-[24px] tracking-[0.5em] font-mono"
            placeholder="000000"
            autoFocus
          />
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px] px-3 py-2" data-testid="2fa-regenerate-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={busy || code.length !== 6}
            data-testid="2fa-regenerate-confirm"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[14px] font-bold uppercase tracking-[0.18em] text-[#0e0e10] disabled:opacity-60"
            style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 50%,#a8871a 100%)' }}
          >
            {busy ? 'Regenerating…' : 'Regenerate codes'}
          </button>
        </form>
      </div>
    </div>
  );
}

function TwoFactorDisableModal({ onClose, onSuccess }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = (code || '').replace(/\s+/g, '');
    if (trimmed.length !== 6) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    setError(''); setBusy(true);
    try {
      const res = await fetch(`${API}/academy/admin/security/2fa/disable`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: trimmed }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
      onSuccess();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center px-4 py-8" data-testid="2fa-disable-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[420px] w-full overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between bg-[#0e0e10] text-white">
          <div className="flex items-center gap-2">
            <ShieldOff className="w-5 h-5 text-amber-400" />
            <div className="text-[16px] font-extrabold">Disable two-factor authentication</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="2fa-disable-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <p className="text-[13px] text-[#6E6E73] leading-relaxed">
            Confirm your current authenticator code to disable 2FA. Future logins will only require a password.
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            data-testid="2fa-disable-code"
            className="w-full px-4 py-3 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] focus:ring-2 focus:ring-[#c9a227]/30 outline-none text-center text-[24px] tracking-[0.5em] font-mono"
            placeholder="000000"
            autoFocus
          />
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px] px-3 py-2" data-testid="2fa-disable-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={busy || code.length !== 6}
            data-testid="2fa-disable-confirm"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[14px] font-bold uppercase tracking-[0.18em] text-white bg-red-600 hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? 'Disabling…' : 'Disable 2FA'}
          </button>
        </form>
      </div>
    </div>
  );
}

